import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import moment from 'moment';
import { DollarSign, Landmark, TrendingDown, TrendingUp, BarChart3 } from 'lucide-react';
import {
    DashboardFilterCard,
    FinanceStatCard,
    ProfitabilityByClientSection,
    ProfitabilityByServiceTypeSection,
    RevenueForecastSection,
    ServiceSummaryCard,
    WorkingCapitalSection
} from './FinancialIntelligenceSections';
import { OrderTypeToColor } from '../common/OrgDashboard/OrgDashboard';
import { useAdvancedDateRangePicker } from '../../hooks/useAdvancedDateRangePicker';
import { makeRESTApiRequest, ABORT_ERROR } from '../../helpers/api';
import { ENDPOINTS } from '../../helpers/constants';

const pageWrapperStyle = {
    minHeight: 'calc(100vh - 64px)',
    background: '#f8fafc'
};

const defaultFilters = {
    client: []
};
const CLIENT_TABLE_PAGE_SIZE = 10;

export const formatUsd = value =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(value) || 0);

const STAT_CARD_DEFS = [
    {
        id: 'totalRevenue',
        title: 'Total Revenue',
        icon: DollarSign,
        tone: 'revenue',
        valueKey: 'totalRevenue',
        getNote: () => null
    },
    {
        id: 'collectedFees',
        title: 'Collected Fees',
        icon: TrendingUp,
        tone: 'success',
        valueKey: 'totalCollectedFees',
        getNote: () => null
    },
    {
        id: 'passThroughFees',
        title: 'Pass-Through Fees',
        icon: Landmark,
        tone: 'warning',
        valueKey: 'totalPassThroughFees',
        getNote: () => null
    },
    {
        id: 'hardCostsLost',
        title: 'Hard Costs Lost',
        icon: TrendingDown,
        tone: 'danger',
        valueKey: 'totalHardCostsLost',
        getNote: () => null
    },
    {
        id: 'operatingResult',
        title: 'Operating Result',
        icon: BarChart3,
        tone: 'success',
        valueKey: 'totalOperatingResult',
        getNote: () => null
    }
];

const DEFAULT_TONE = '#6B7280';

/** Maps API profitability row → ProfitabilityServiceCard props (metrics layout). */
function mapProfitabilityByServiceTypeRow(row) {
    const op = Number(row?.operatingResult) || 0;
    const serviceType = row?.serviceType ?? '';
    return {
        title: serviceType,
        toneColor: OrderTypeToColor[serviceType] || DEFAULT_TONE,
        metrics: [
            { label: 'Paid', value: row?.paid ?? 0 },
            { label: 'Revenue', value: formatUsd(row?.revenue) },
            { label: 'Avg Fee', value: formatUsd(row?.avgCollectedFees) },
            { label: 'Avg HC', value: formatUsd(row?.avgHardCost) },
            { label: 'HC Lost', value: formatUsd(row?.hardCostsLost) },
            { label: 'Cancelled', value: row?.cancelled ?? 0 },
            {
                label: 'Operating Result',
                value: formatUsd(row?.operatingResult),
                valueClassName: op >= 0 ? 'text-success' : 'text-danger'
            }
        ]
    };
}

const FinancialIntelligence = ({ currentUserInfo }) => {
    const [filters, setFilters] = useState(defaultFilters);
    const initialDateRange = useMemo(
        () => ({
            startDate: moment()
                .startOf('month')
                .toDate(),
            endDate: moment()
                .endOf('month')
                .toDate(),
            key: 'selection',
            rangeType: 'This Month'
        }),
        []
    );
    const { dateRange, setDateRange } = useAdvancedDateRangePicker(initialDateRange);
    const { dateRange: clientTableDateRange, setDateRange: setClientTableDateRange } = useAdvancedDateRangePicker(
        initialDateRange
    );
    const [financeData, setFinanceData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [clientTableState, setClientTableState] = useState({
        rows: [],
        loading: true,
        pageNumber: 1,
        totalPages: 1,
        resultCount: 0,
        sortConfig: { key: 'clientName', direction: 'asc' }
    });

    const [loadingMoreProfitByClient, setLoadingMoreProfitByClient] = useState(false);

    const organizationOptions = useMemo(() => {
        const organizations = currentUserInfo?.organization || [];

        const mappedOrganizations = organizations
            .map(org => ({
                value: org._id,
                label: org.name
            }))
            .sort((a, b) => a.label.localeCompare(b.label));

        return mappedOrganizations;
    }, [currentUserInfo?.organization]);

    const filterOptions = useMemo(
        () => ({
            client: organizationOptions
        }),
        [organizationOptions]
    );

    const handleOrganizationChange = selectedValues => {
        const selectedIds = selectedValues.map(item => item.value);
        setFilters(previousFilters => ({
            ...previousFilters,
            client: selectedIds
        }));
    };

    const loadFinancialStats = useCallback(async () => {
        setLoading(true);
        setError(null);

        // ISO strings required: replaceDatesInJson turns raw Date into {} (Object.entries(date) is empty).
        const startDate = dateRange.startDate ? moment(dateRange.startDate).startOf('day') : null;
        const endDate = dateRange.endDate ? moment(dateRange.endDate).endOf('day') : null;

        const { response, error: requestError } = await makeRESTApiRequest({
            endpoint: ENDPOINTS.FINANCIAL_INTELLIGENCE,
            method: 'POST',
            requestBody: {
                organizationIds: filters.client,
                dateFilter: {
                    startDate: startDate?.toISOString() ?? null,
                    endDate: endDate?.toISOString() ?? null
                }
            }
        });

        if (requestError) {
            if (requestError !== ABORT_ERROR) {
                setError(requestError);
            }
            setFinanceData(null);
            setLoading(false);
            return;
        }

        setFinanceData(response?.data ?? null);
        setLoading(false);
    }, [filters.client, dateRange.startDate, dateRange.endDate, dateRange.rangeType]);

    const loadProfitabilityByClientTable = useCallback(
        async (page = 1, append = false) => {
            if (append) {
                setLoadingMoreProfitByClient(true);
            } else {
                setClientTableState(prev => ({ ...prev, loading: true }));
            }

            const startDate = clientTableDateRange.startDate ? moment(clientTableDateRange.startDate).startOf('day') : null;
            const endDate = clientTableDateRange.endDate ? moment(clientTableDateRange.endDate).endOf('day') : null;

            const { response, error: requestError } = await makeRESTApiRequest({
                endpoint: ENDPOINTS.FINANCIAL_INTELLIGENCE_PROFITABILITY_BY_CLIENT,
                method: 'POST',
                requestBody: {
                    organizationIds: filters.client,
                    dateFilter: {
                        startDate: startDate?.toISOString() ?? null,
                        endDate: endDate?.toISOString() ?? null
                    },
                    pageNumber: page,
                    pageSize: CLIENT_TABLE_PAGE_SIZE,
                    sortBy: clientTableState.sortConfig.key,
                    sortDir: clientTableState.sortConfig.direction
                }
            });

            if (requestError) {
                if (requestError !== ABORT_ERROR) {
                    setError(requestError);
                }
                if (!append) {
                    setClientTableState(prev => ({
                        ...prev,
                        loading: false,
                        rows: [],
                        totalPages: 1,
                        resultCount: 0
                    }));
                } else {
                    setLoadingMoreProfitByClient(false);
                }
                return;
            }

            const tableData = response?.data || {};
            setClientTableState(prev => ({
                ...prev,
                loading: false,
                rows: append ? [...prev.rows, ...(tableData.results || [])] : tableData.results || [],
                pageNumber: tableData.pageNumber || page,
                totalPages: tableData.totalPages || 1,
                resultCount: tableData.resultCount || 0
            }));
            if (append) setLoadingMoreProfitByClient(false);
        },
        [
            filters.client,
            clientTableDateRange.startDate,
            clientTableDateRange.endDate,
            clientTableState.sortConfig.key,
            clientTableState.sortConfig.direction
        ]
    );

    const handleLoadMore = useCallback(() => {
        if (!loadingMoreProfitByClient && clientTableState.pageNumber < clientTableState.totalPages) {
            const nextPage = clientTableState.pageNumber + 1;
            loadProfitabilityByClientTable(nextPage, true);
        }
    }, [
        loadingMoreProfitByClient,
        clientTableState.pageNumber,
        clientTableState.totalPages,
        loadProfitabilityByClientTable
    ]);

    useEffect(() => {
        loadFinancialStats();
    }, [loadFinancialStats]);

    useEffect(() => {
        loadProfitabilityByClientTable();
    }, [loadProfitabilityByClientTable]);

    useEffect(() => {
        setClientTableState(prev => ({
            ...prev,
            pageNumber: 1
        }));
    }, [
        filters.client,
        clientTableDateRange.startDate,
        clientTableDateRange.endDate,
        clientTableDateRange.rangeType
    ]);

    const handleClientTableSortChange = useCallback(newSortConfig => {
        setClientTableState(prev => ({
            ...prev,
            sortConfig: newSortConfig,
            pageNumber: 1
        }));
    }, []);

    const statCards = useMemo(() => {
        const stats = financeData?.financeStats;
        const rev = stats?.totalRevenue ?? 0;

        return STAT_CARD_DEFS.map(def => {
            const raw = stats?.[def.valueKey] ?? 0;
            const value = loading ? '…' : formatUsd(raw);
            const note = def.getNote ? def.getNote(stats, { rev, loading }) : null;
            const tone = Number(raw) < 0 ? 'danger' : def.tone;

            return {
                id: def.id,
                title: def.title,
                value,
                note,
                icon: def.icon,
                tone
            };
        });
    }, [financeData, loading]);

    const serviceMetrics = useMemo(() => {
        const summary = financeData?.serviceSummary;
        return {
            title: 'Services',
            items: [
                {
                    label: 'Paid',
                    value: loading ? '…' : String(summary?.paid ?? 0),
                    toneClass: 'text-success'
                },
                {
                    label: 'Cancelled',
                    value: loading ? '…' : String(summary?.cancelled ?? 0),
                    toneClass: 'text-danger'
                }
            ]
        };
    }, [financeData?.serviceSummary, loading]);

    const profitabilityByServiceCards = useMemo(
        () => (financeData?.profitabilityByServiceType ?? []).map(mapProfitabilityByServiceTypeRow),
        [financeData?.profitabilityByServiceType]
    );

    const workingCapitalSummary = useMemo(() => {
        const w = financeData?.workingCapitalOutstanding;
        const money = v => (loading ? '…' : formatUsd(v ?? 0));
        return [
            {
                title: 'Total Accounts Receivable',
                value: money(w?.totalAccountsReceivable),
                valueClassName: (w?.totalAccountsReceivable || 0) < 0 ? 'text-danger' : 'text-dark',
                isNeg: (w?.totalAccountsReceivable || 0) < 0
            },
            {
                title: 'Working Capital (Hard Costs)',
                value: money(w?.totalWorkingCapital),
                valueClassName: (w?.totalWorkingCapital || 0) < 0 ? 'text-danger' : 'text-info',
            },
            {
                title: 'Pending Fees (Your Earnings)',
                value: money(w?.totalPendingFees),
                valueClassName: (w?.totalPendingFees || 0) < 0 ? "text-danger" : 'text-success'
            }
        ];
    }, [financeData?.workingCapitalOutstanding, loading]);

    const workingCapitalRows = useMemo(() => {
        const wcRows = financeData?.workingCapitalOutstanding?.byServiceType;
        if (loading) {
            const skeletonTypes = ['Lien Search', 'Estoppel', 'Survey', 'Total'];
            return skeletonTypes.map(serviceType => ({
                serviceType,
                unpaidCount: '…',
                accountsReceivable: '…',
                workingCapital: '…',
                pendingFees: '…',
                isTotal: serviceType === 'Total'
            }));
        }
        if (!wcRows?.length) {
            return ['Lien Search', 'Estoppel', 'Survey', 'Total'].map(serviceType => ({
                serviceType,
                unpaidCount: 0,
                accountsReceivable: 0,
                workingCapital: 0,
                pendingFees: 0,
                isTotal: serviceType === 'Total'
            }));
        }
        return wcRows.map(row => ({
            serviceType: row.serviceType,
            unpaidCount: row.unpaidCount,
            accountsReceivable: (row.accountsReceivable),
            workingCapital: (row.workingCapital),
            pendingFees: (row.pendingFees),
            isTotal: Boolean(row.isTotal)
        }));
    }, [financeData?.workingCapitalOutstanding, loading]);

    const revenueForecastRows = useMemo(() => {
        const rvfRows = financeData?.revenueForecast?.byMonth;
        if (loading) {
            return Array.from({ length: 7 }, (_, i) => ({
                month: i < 6 ? '…' : 'Total',
                services: '…',
                expectedRevenue: '…',
                estimatedFees: '…',
                estimatedHardCosts: '…',
                isTotal: i === 6
            }));
        }
        if (!rvfRows?.length) {
            const fallback = [];
            for (let i = 0; i < 6; i += 1) {
                fallback.push({
                    month: moment()
                        .add(i, 'month')
                        .format('MMM YYYY'),
                    services: 0,
                    expectedRevenue: 0,
                    estimatedFees: 0,
                    estimatedHardCosts: 0
                });
            }
            fallback.push({
                month: 'Total',
                services: 0,
                expectedRevenue: 0,
                estimatedFees: 0,
                estimatedHardCosts: 0,
                isTotal: true
            });
            return fallback;
        }
        return rvfRows.map(row => ({
            month: row.month,
            services: row.services,
            expectedRevenue: row.expectedRevenue,
            estimatedFees: row.estimatedFees,
            estimatedHardCosts: row.estimatedHardCosts,
            isTotal: Boolean(row.isTotal)
        }));
    }, [financeData?.revenueForecast, loading]);

    return (
        <div className="w-100 px-3 px-md-5 px-lg-5 py-3" style={pageWrapperStyle}>
            <div className="mb-4">
                <h2 className="font-weight-bold text-dark mb-1">Financial Intelligence</h2>
                <p className="text-muted smallFont mb-0">
                    Internal management dashboard for financial tracking and profitability analysis
                </p>
            </div>

            <DashboardFilterCard
                options={filterOptions}
                values={filters}
                onOrganizationChange={handleOrganizationChange}
                dateRange={dateRange}
                onDateRangeChange={ranges => setDateRange(ranges.selection)}
            />

            <Row className="mb-1">
                {statCards.slice(0, 3).map(({ id, ...card }) => (
                    <Col key={id} xs={12} lg={4} className="mb-3">
                        <FinanceStatCard {...card} loading={loading} />
                    </Col>
                ))}
            </Row>

            <Row className="mb-2">
                {statCards.slice(3, 5).map(({ id, ...card }) => (
                    <Col key={id} xs={12} lg={4} className="mb-3">
                        <FinanceStatCard {...card} loading={loading} />
                    </Col>
                ))}
                <Col xs={12} lg={4} className="mb-3">
                    <ServiceSummaryCard {...serviceMetrics} loading={loading} />
                </Col>
            </Row>

            <ProfitabilityByServiceTypeSection services={profitabilityByServiceCards} loading={loading} />
            <ProfitabilityByClientSection
                rows={clientTableState.rows}
                loading={clientTableState.loading}
                pageNumber={clientTableState.pageNumber}
                pageSize={CLIENT_TABLE_PAGE_SIZE}
                totalPages={clientTableState.totalPages}
                resultCount={clientTableState.resultCount}
                sortConfig={clientTableState.sortConfig}
                onSortChange={handleClientTableSortChange}
                onBottomReached={handleLoadMore}
                loadingMoreData={loadingMoreProfitByClient}
                dateRange={clientTableDateRange}
                onDateRangeChange={ranges => setClientTableDateRange(ranges.selection)}
            />
            <WorkingCapitalSection summaryCards={workingCapitalSummary} rows={workingCapitalRows} loading={loading} />
            <RevenueForecastSection rows={revenueForecastRows} loading={loading} />
        </div>
    );
};

export default FinancialIntelligence;
